/*
 * Seamless ticker. The track is repeated until it is at least as wide as its
 * container, then duplicated once more inside a single run. The run slides by
 * half its width, so the second copy is already in place when the first
 * scrolls out. Everything beyond the first pass is hidden from assistive
 * tech. Speed is derived from one pass so short and long tracks travel at
 * the same pace.
 *
 * Images load eagerly. Safari drops lazy images once a transform moves them,
 * which reads as the logos scrolling halfway and then disappearing.
 */

const SELECTOR = '[data-marquee]'
const PIXELS_PER_SECOND = 70
const MAX_PASSES = 12

const templates = new WeakMap()

function hideFromAssistiveTech(node) {
	node.setAttribute('aria-hidden', 'true')
	node.querySelectorAll('a, button, input, [tabindex]').forEach((el) => {
		el.setAttribute('tabindex', '-1')
	})
}

function warmImages(root) {
	root.querySelectorAll('img').forEach((img) => {
		const wasLazy = img.loading === 'lazy'
		img.loading = 'eager'
		img.decoding = 'async'

		const sizes = img.getAttribute('sizes')
		if (sizes && /^auto(,|$)/.test(sizes)) {
			const next = sizes.replace(/^auto,\s*/, '')
			if (next) img.setAttribute('sizes', next)
			else img.removeAttribute('sizes')
		}

		/* A lazy image Safari has already skipped will not start on its own. */
		if (wasLazy && !img.complete && img.getAttribute('src')) {
			img.src = img.src
		}
	})
}

function fill(marquee, track) {
	const target = marquee.getBoundingClientRect().width
	const pass = Array.from(track.children)
	if (!pass.length) return

	let guard = 0
	while (track.getBoundingClientRect().width < target && guard < MAX_PASSES) {
		pass.forEach((node) => {
			const copy = node.cloneNode(true)
			hideFromAssistiveTech(copy)
			warmImages(copy)
			track.appendChild(copy)
		})
		guard += 1
	}
}

function setup(marquee) {
	const track = marquee.querySelector('.marquee__track:not([aria-hidden="true"])')
	if (!track) return

	if (!templates.has(marquee)) templates.set(marquee, track.innerHTML)

	const run = marquee.querySelector('.marquee__run')
	if (run) {
		marquee.insertBefore(track, run)
		run.remove()
	}

	marquee.querySelectorAll('.marquee__track[aria-hidden="true"]').forEach((el) => el.remove())
	track.innerHTML = templates.get(marquee)
	warmImages(track)

	fill(marquee, track)

	const width = track.getBoundingClientRect().width
	if (!width) return

	const clone = track.cloneNode(true)
	hideFromAssistiveTech(clone)
	warmImages(clone)

	const next = document.createElement('div')
	next.className = 'marquee__run'
	track.parentNode.insertBefore(next, track)
	next.append(track, clone)

	marquee.style.setProperty('--marquee-duration', `${Math.max(width / PIXELS_PER_SECOND, 12).toFixed(1)}s`)
}

export default function initMarquee() {
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

	const marquees = Array.from(document.querySelectorAll(SELECTOR))
	if (!marquees.length) return

	const run = () => marquees.forEach(setup)

	run()

	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(run).catch(() => {})
	}

	/* Logos usually decode after init, which changes the track width. */
	window.addEventListener('load', run, { once: true })

	let timer = null
	window.addEventListener(
		'resize',
		() => {
			window.clearTimeout(timer)
			timer = window.setTimeout(run, 200)
		},
		{ passive: true }
	)
}
